import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Brain, 
  Route, 
  Target, 
  Network, 
  BarChart2, 
  Code2, 
  LineChart,
  Sparkles,
  Building2,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

export function HomePage() {
  const features = [
    {
      icon: <Brain className="w-6 h-6 text-blue-500" />,
      title: "Exclusive DSA Content",
      description: "Access highly curated content you won't find anywhere else, crafted by industry experts."
    },
    {
      icon: <Route className="w-6 h-6 text-green-500" />,
      title: "Optimized Learning Roadmap",
      description: "Master DSA concepts in minimal time with our carefully structured learning paths."
    },
    {
      icon: <Target className="w-6 h-6 text-purple-500" />,
      title: "Interview-Focused Practice",
      description: "Prepare effectively with content tailored specifically for technical interviews."
    },
    {
      icon: <Network className="w-6 h-6 text-orange-500" />,
      title: "Interactive Mind Maps",
      description: "Visualize connections between concepts and company-specific OA patterns."
    },
    {
      icon: <BarChart2 className="w-6 h-6 text-red-500" />,
      title: "Progress Tracking",
      description: "Monitor your learning journey with our comprehensive dashboard."
    },
    {
      icon: <Code2 className="w-6 h-6 text-indigo-500" />,
      title: "Interactive Platform",
      description: "Practice and verify your solutions with our built-in code editor and tester."
    },
    {
      icon: <LineChart className="w-6 h-6 text-cyan-500" />,
      title: "Learning Analytics",
      description: "Get insights into your strengths and areas needing improvement."
    },
    {
      icon: <Sparkles className="w-6 h-6 text-yellow-500" />,
      title: "Smart Recommendations",
      description: "Receive personalized question suggestions based on your performance."
    },
    {
      icon: <Building2 className="w-6 h-6 text-emerald-500" />,
      title: "Company OA Practice",
      description: "Practice with real Online Assessment questions from top companies."
    }
  ];

  const companies = [
    'Google', 'Amazon', 'Microsoft', 'Meta', 'Apple', 'Netflix'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 transform -skew-y-6"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
          <div className="text-center">
            <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
              <span className="block">Master DSA in Weeks,</span>
              <span className="block text-blue-600">Not Years</span>
            </h1>
            <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
              Stop wasting years figuring out Data Structures & Algorithms. 
              Our curated content and structured approach will get you interview-ready in weeks.
            </p>
            <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
              <div className="rounded-md shadow">
                <Link
                  to="/coming-soon"
                  className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 md:py-4 md:text-lg md:px-10"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-extrabold text-gray-900">
            Everything You Need to Master DSA
          </h2>
          <p className="mt-4 text-xl text-gray-600">
            Comprehensive features designed to accelerate your learning journey
          </p>
        </div>
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, index) => (
            <div
              key={index}
              className="relative group bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300"
            >
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Companies Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Prepare for Top Tech Companies
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              Practice with real interview questions from leading tech companies
            </p>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-8 md:grid-cols-3 lg:grid-cols-6">
            {companies.map((company, index) => (
              <div
                key={index}
                className="col-span-1 flex justify-center items-center py-8 px-8 bg-white rounded-lg shadow-sm"
              >
                <span className="text-xl font-semibold text-gray-700">{company}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-extrabold text-gray-900">
            Why Choose Our Platform?
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <ul className="space-y-4">
              {[
                'Highly curated content not found elsewhere',
                'Structured learning path for optimal progress',
                'Interactive practice environment',
                'Real-time progress tracking',
                'Company-specific preparation'
              ].map((point, index) => (
                <li key={index} className="flex items-center space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">{point}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-6 rounded-xl text-white">
            <h3 className="text-2xl font-bold mb-4">Ready to Transform Your DSA Journey?</h3>
            <p className="mb-6">Join thousands of successful developers who mastered DSA using our platform.</p>
            <Link
              to="/auth"
              className="inline-block px-6 py-3 bg-white text-blue-600 rounded-md font-medium hover:bg-gray-100 transition-colors duration-300"
            >
              Start Learning Now
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}